import {
  InferOutput,
  number,
  object,
  optional,
  safeParse,
  string,
  ValiError,
} from 'valibot';
import { FetchError, L } from '../utils.ts';

const API_DOMAIN = import.meta.env.VITE_LICENSE_API_URL;

/**
 * Schema for the parameters of the `/v1/auth/token` endpoint.
 */
export const AuthTokenRequestArgsSchema = object({
  version: string(),
  signature: string(),
  ecdh_public_key: string(),
  timestamp: number(),
});

/**
 * Type for the parameters of the `/v1/auth/token` endpoint.
 */
export type AuthTokenRequestArgs = InferOutput<
  typeof AuthTokenRequestArgsSchema
>;

/**
 * Schema for the response of the `/v1/auth/token` endpoint.
 */
export const AuthTokenResponseSchema = object({
  message: string(),
  ecdh_public_key: string(),
  salt: string(),
  iv: string(),
  token: string(),
  authTag: string(),
  expires: optional(number()),
});

/**
 * Type for the response of the `/v1/auth/token` endpoint.
 */
export type AuthTokenResponse = InferOutput<typeof AuthTokenResponseSchema>;

/**
 * Request an authentication token from the license server.
 * @param {AuthTokenRequestArgs} params The parameters for the request.
 * @returns {Promise<AuthTokenResponse>} The authentication token response.
 */
export const $requestAuthToken = async (
  params: AuthTokenRequestArgs,
): Promise<AuthTokenResponse> => {
  const p = safeParse(AuthTokenRequestArgsSchema, params);
  if (!p.success) {
    throw new ValiError(p.issues);
  }
  const { version, signature, ecdh_public_key, timestamp } = p.output;

  const res = await fetch(`${API_DOMAIN}/v1/auth/token`, {
    method: 'POST',
    headers: {
      Authorization: `HMAC-SHA256 key="${version}:${signature}"`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      ecdh_public_key,
      timestamp,
    }),
  });

  if (!res.ok) {
    const message = `Error retrieving token (${res.status})`;
    throw new FetchError(message, res.status);
  }

  const expires = res.headers.get('expires');
  L.info(`Token expires at ${expires}`);

  const data = await res.json();
  const parsedRes = safeParse(AuthTokenResponseSchema, data);
  if (!parsedRes.success) {
    throw new ValiError(parsedRes.issues);
  }
  return {
    ...parsedRes.output,
    expires: expires ? new Date(expires).getTime() : undefined,
  };
};
